import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React from 'react';
import { Modal, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import { useAuth } from '@/lib/auth/AuthProvider';

type IoniconName = keyof typeof Ionicons.glyphMap;

interface MoreItem {
  icon: IoniconName;
  label: string;
  onPress?: () => void;
  soon?: boolean;
}

function GridItem({ item }: { item: MoreItem }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={item.label}
      disabled={item.soon}
      onPress={item.onPress}
      style={{ width: '31.5%' }}
      className={`mb-3 items-center gap-2 rounded-card border border-glass-border bg-glass px-2 py-4 ${
        item.soon ? 'opacity-50' : 'active:bg-surface-2'
      }`}
    >
      <View className="h-10 w-10 items-center justify-center rounded-full bg-volt-glass">
        <Ionicons name={item.icon} size={20} color={palette.voltBright} />
      </View>
      <AppText variant="micro" weight="bold" className="text-center text-ink" numberOfLines={1}>
        {item.label}
      </AppText>
      {item.soon ? (
        <AppText variant="micro" className="text-muted" style={{ fontSize: 9 }}>
          {t('more.comingSoon')}
        </AppText>
      ) : null}
    </Pressable>
  );
}

/**
 * The top "More" sheet: secondary destinations in a clean 3×2 grid with the
 * cohesive Ionicons set. Transfer Center opens the live mercato feed; Settings
 * uses the gear (cog), not a sun.
 */
export function MoreMenu({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { session } = useAuth();

  const go = (path: string) => {
    onClose();
    router.push(path as never);
  };

  const items: MoreItem[] = [
    { icon: 'git-network', label: t('predictor.title'), onPress: () => go('/predictor') },
    { icon: 'newspaper', label: t('news.title'), onPress: () => go('/news') },
    { icon: 'analytics', label: t('more.predictor'), onPress: () => go('/predictions') },
    { icon: 'swap-horizontal', label: t('more.transferCenter'), onPress: () => go('/transfers') },
    { icon: 'notifications', label: t('more.notifications'), onPress: () => go('/notifications') },
    { icon: 'help-circle', label: t('howTo.title'), onPress: () => go('/how-to-play') },
    { icon: 'settings', label: t('more.settings'), onPress: () => go('/profile') },
  ];

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <Pressable onPress={onClose} style={{ flex: 1, backgroundColor: 'rgba(5,5,14,0.72)' }}>
        <View style={{ paddingTop: 50 }}>
          <Pressable onPress={() => undefined}>
            <View
              className="border-b border-glass-border px-4 pb-6 pt-4"
              style={{ backgroundColor: palette.gradMid, borderBottomLeftRadius: 26, borderBottomRightRadius: 26 }}
            >
              <View className="flex-row items-center">
                <AppText variant="title">{t('more.title')}</AppText>
                <View className="flex-1" />
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('common.back')}
                  onPress={onClose}
                  className="h-8 w-8 items-center justify-center rounded-full bg-surface-2"
                >
                  <Ionicons name="close" size={18} color={palette.soft} />
                </Pressable>
              </View>

              <Pressable
                accessibilityRole="button"
                onPress={() => go('/profile')}
                className="mt-4 flex-row items-center gap-3 rounded-card border border-glass-border bg-glass px-4 py-3.5 active:bg-surface-2"
              >
                <View className="h-9 w-9 items-center justify-center rounded-full bg-volt-glass">
                  <Ionicons name="person" size={18} color={palette.voltBright} />
                </View>
                <View className="flex-1">
                  <AppText variant="caption" weight="bold" className="text-ink">
                    {session ? t('more.profile') : t('profile.guestTitle')}
                  </AppText>
                  <AppText variant="micro" className="leading-4 text-muted">
                    {t('more.profileSub')}
                  </AppText>
                </View>
                {!session ? (
                  <View className="rounded-full bg-volt px-4 py-2">
                    <AppText variant="micro" weight="bold" className="text-on-volt">
                      {t('auth.signIn')}
                    </AppText>
                  </View>
                ) : (
                  <Ionicons name="chevron-forward" size={16} color={palette.muted} />
                )}
              </Pressable>

              <View className="mt-4 flex-row flex-wrap justify-between">
                {items.map((item) => (
                  <GridItem key={item.label} item={item} />
                ))}
              </View>
            </View>
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  );
}
